import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Box,
  Typography,
  useTheme,
  alpha,
  Chip
} from "@mui/material";
import { Language as LanguageIcon } from '@mui/icons-material';

const LanguageSwitcher = ({ onLanguageChange }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const languages = [
    { code: "en", name: "English", native: "English" },
    { code: "hi", name: "Hindi", native: "हिंदी" },
    { code: "mr", name: "Marathi", native: "मराठी" },
    { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
    { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
    { code: "ta", name: "Tamil", native: "தமிழ்" },
    { code: "te", name: "Telugu", native: "తెలుగు" },
    { code: "or", name: "Odia", native: "ଓଡିଆ" },
    { code: "bn", name: "Bengali", native: "বাংলা" },
    { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  ];

  // Handle language selection
  const handleChange = (event) => {
    const lang = event.target.value;
    setSelectedLanguage(lang);
    setOpenConfirmation(true);
  };

  // Confirm language change
  const handleConfirm = () => {
    setOpenConfirmation(false);
    localStorage.clear();

    setTimeout(() => {
      if (onLanguageChange) {
        onLanguageChange(selectedLanguage);
      }
      i18n.changeLanguage(selectedLanguage);
    }, 100);
  };

  // Cancel language change
  const handleCancel = () => {
    setOpenConfirmation(false);
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language);

  return (
    <>
      <FormControl variant="outlined" size="small">
        <InputLabel id="language-select-label">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageIcon fontSize="small" />
            <Typography variant="body2">Language</Typography>
          </Box>
        </InputLabel>
        <Select
          labelId="language-select-label"
          value={i18n.language}
          onChange={handleChange}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon fontSize="small" />
              <Typography variant="body2">Language</Typography>
            </Box>
          }
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          }}
        >
          {languages.map((language) => (
            <MenuItem key={language.code} value={language.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {language.native}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {language.name}
                  </Typography>
                </Box>
                {language.code === i18n.language && (
                  <Chip
                    label="Current"
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{ ml: 'auto', fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Confirmation Dialog */}
      <Dialog 
        open={openConfirmation} 
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <LanguageIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {t("confimLanguageChangeTitle")}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t("confirmLanguageChange")}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <LanguageIcon color="primary" />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {languages.find(lang => lang.code === selectedLanguage)?.native}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {languages.find(lang => lang.code === selectedLanguage)?.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            autoFocus
            sx={{
              minWidth: 100,
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LanguageSwitcher;